import React from 'react';
import { styled, List, Divider, Button } from '@mui/material';
import { getUser } from '../../../../../utils';
import getNavigationConfig from './configs';
import { canAccessItem, getBookmarksConfig } from './utils/navPermissions';
import { useFetchUsers } from '../../../../data/generic-queries/users';
import { NavSearch, NavGroupRenderer, NavItemRenderer } from './components';
import MMLogo from '../shared/icons/MMLogo.png';
import MMLogoHorizontal from '../shared/icons/MMLogoHorizontal.png';
import { NAV_V2_NATIVE_REGIONS } from '../../../../constants/region-utils';

const NavWrap = styled('div')(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    width: '100%',
    height: '100%',
    overflow: 'auto',
    color: theme.palette.nav.text.light,
    backgroundColor: theme.palette.nav.background.dark,
    '& .MuiTypography-root': { color: theme.palette.nav.text.light },
    '&::-webkit-scrollbar': {
        display: 'none',
    },
    scrollbarWidth: 'none',
    msOverflowStyle: 'none',
    svg: {
        color: theme.palette.background.light,
    },
}));

const Header = styled('div')(({ theme }) => ({
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    fontSize: '16px',
    fontWeight: 500,
    marginLeft: theme.spacing(2),
    marginTop: theme.spacing(0.75),
    img: {
        marginTop: theme.spacing(1),
        marginRight: theme.spacing(1),
    },
}));

const Footer = styled('div')(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    fontSize: '12px',
    color: theme.palette.nav.text.med,
    fontWeight: 600,
    marginBottom: theme.spacing(1),
    '.top-line': {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        img: {
            marginLeft: theme.spacing(1),
        },
    },
    '.version': {
        display: 'flex',
        justifyContent: 'center',
        width: '100%',
        fontSize: '9px',
        marginTop: theme.spacing(1),
    },
}));

const NavDivider = styled(Divider)(({ theme }) => ({
    opacity: 0.31,
    backgroundColor: theme.palette.nav.divider.light,
}));

const NavV2 = ({ toggleLegacyNav }) => {
    const config = getNavigationConfig();
    const user = getUser();
    const { region } = user;
    const showToggle = !NAV_V2_NATIVE_REGIONS.includes(region);

    const { data } = useFetchUsers({
        where: {
            _id: user.id,
        },
        project: { navigation_bookmarks: 1 },
        enabled: !!user.id,
        key: 'navigation_bookmarks',
    });
    const bookmarks = data?.[0]?.navigation_bookmarks || [];
    const bookmarksConfig = getBookmarksConfig(bookmarks, config, user);

    return (
        <NavWrap>
            <div>
                <Header>
                    <img src={MMLogo} height={38} width={38} alt="Milk Moovement logo" />
                    Milk Moovement
                </Header>
                <NavDivider sx={{ mt: 1.5 }} />
                <NavSearch user={user} />
                <List disablePadding>
                    {bookmarksConfig && (
                        <>
                            <NavGroupRenderer
                                key={bookmarksConfig.id}
                                item={bookmarksConfig}
                                user={user}
                                bookmarks={bookmarks} // wrap
                            />
                            <NavDivider sx={{ my: 1 }} />
                        </>
                    )}
                    {config.map((item) => {
                        if (!canAccessItem(user, item)) return null;
                        if (item.type === 'group') {
                            return <NavGroupRenderer key={item.id} item={item} user={user} bookmarks={bookmarks} />;
                        }

                        if (item.type === 'divider') {
                            return <NavDivider key={item.id} sx={{ my: 1 }} />;
                        }

                        return <NavItemRenderer key={item.id} item={item} user={user} bookmarks={bookmarks} />;
                    })}
                </List>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                <NavDivider sx={{ mt: 1.5, mb: 2 }} />
                <Footer>
                    <div className="top-line">
                        Powered by
                        <img src={MMLogoHorizontal} height={28} width={103} alt="Milk Moovement logo" />
                    </div>
                    {showToggle && (
                        <Button
                            size="small"
                            variant="text"
                            onClick={toggleLegacyNav}
                            sx={{
                                mt: 1, // wrap
                                opacity: 0.7,
                                fontSize: '1rem',
                            }}
                        >
                            Old Navigation
                        </Button>
                    )}
                    {process.env.REACT_APP_VERSION && <div className="version">v. {process.env.REACT_APP_VERSION.substring(0, 7)}</div>}
                </Footer>
            </div>
        </NavWrap>
    );
};

export default NavV2;
